import moment from 'moment'
import { convertObjectSnakeCaseToCamelCaseProperties } from '../../../../kernel/mappers'
import { FULL_DATE_TIME_FORMAT_WITH_T, getFormattedDate } from '../../../../utils/dates'
import { JobItem } from '../../domain/items'
import { DeliveryCodeInfo, Job, Location } from '../../domain/job'
import {
  DeliveryOptionKeys,
  DeliveryOptions,
  Item,
  ItemState,
  LocationResponse,
  NebulaJob,
  ResourceInformation,
  Step as StepNebula,
  Task as TaskNebula
} from '../../domain/job-nebula'
import { Step, Task, TaskResource, TaskState } from '../../domain/task'

export const mapNebulaJobToOldJob = (nebulaJob: NebulaJob): Job => {
  const deliveryCodeInfo = getDeliveryCodeInfo(nebulaJob.delivery_options)

  const job: Job = {
    uuid: nebulaJob.id,
    jobNumber: nebulaJob.job_number,
    reference: nebulaJob.job_number,
    jobComment: nebulaJob.job_comment,
    origin: buildLocation(nebulaJob.origin),
    destination: buildLocation(nebulaJob.destination),
    receiver: {
      email: nebulaJob.recipient.email,
      firstName: nebulaJob.recipient.name,
      identification: '',
      lastName: '',
      phoneNumber: nebulaJob.recipient.phone_number
    },
    paymentMethod: nebulaJob.payment_info.payment.method,
    paymentDetail: nebulaJob.payment_info.payment.method_details,
    paymentReference: nebulaJob.payment_info.payment.reference,
    paymentSucceeded: false,
    updatedPrices: {
      discounts: nebulaJob.payment_info.prices.discounts,
      paymentValue: nebulaJob.collect_with.payment.amount,
      shippingFee: nebulaJob.payment_info.prices.shipping_fee,
      subtotal: nebulaJob.payment_info.prices.subtotal,
      taxes: nebulaJob.payment_info.prices.taxes
    },
    status: nebulaJob.state,
    issueOf: nebulaJob.issue_of,
    itemsStatusCount: getItemsStatusCount(nebulaJob.items),
    storeId: nebulaJob.store.id,
    storeTimezone: nebulaJob.store.time_zone,
    storeReference: nebulaJob.store.reference,
    operationModel: nebulaJob.operational_model,
    createdAt: nebulaJob.created_at,
    updatedAt: nebulaJob.updated_at,
    timestamp: `${moment(nebulaJob.updated_at).valueOf()}`,
    isBigOrder: nebulaJob.is_big_order,
    deliveryCode: deliveryCodeInfo ? deliveryCodeInfo.deliveryCode : '0000',
    deliveryCodeTries: deliveryCodeInfo ? deliveryCodeInfo.deliveryCodeTries : 0,
    deliveryCodeTriesLimit: deliveryCodeInfo ? deliveryCodeInfo.deliveryCodeTriesLimit : 3,
    storeTzCreatedAt: getFormattedDate({
      date: nebulaJob.created_at,
      format: FULL_DATE_TIME_FORMAT_WITH_T,
      timezoneIn: 'Etc/UTC',
      timezoneOut: nebulaJob.store.time_zone
    }),
    storeTzUpdatedAt: getFormattedDate({
      date: nebulaJob.updated_at,
      format: FULL_DATE_TIME_FORMAT_WITH_T,
      timezoneIn: 'Etc/UTC',
      timezoneOut: nebulaJob.store.time_zone
    }),
    storeTzSlotFrom: getFormattedDate({
      date: nebulaJob.slot.from,
      format: FULL_DATE_TIME_FORMAT_WITH_T,
      timezoneIn: 'Etc/UTC',
      timezoneOut: nebulaJob.store.time_zone
    }),
    storeTzSlotTo: getFormattedDate({
      date: nebulaJob.slot.to,
      format: FULL_DATE_TIME_FORMAT_WITH_T,
      timezoneIn: 'Etc/UTC',
      timezoneOut: nebulaJob.store.time_zone
    }),
    slotFrom: nebulaJob.slot.from,
    slotTo: nebulaJob.slot.to,
    tasks: nebulaJob.tasks.map((task) => mapTaskModelToTask(task)),
    items: nebulaJob.items.map((item) => mapItemToJobItem(item)),
    paymentValue: nebulaJob.payment_info.prices.order_value,
    shippingFee: nebulaJob.payment_info.prices.shipping_fee,
    logisticCostCurrency: nebulaJob.payment_info.currency_code,
    source: nebulaJob.source,
    externalData: nebulaJob.external_data
      ? {
          backoffice: nebulaJob.external_data.backoffice || {}
        }
      : null,
    invoices: {
      value: nebulaJob.payment_info.prices.order_value,
      photosUrl: nebulaJob.payment_info.invoice?.attachments,
      billingNumber: nebulaJob.payment_info.invoice?.reference
    },
    paymentInfo: convertObjectSnakeCaseToCamelCaseProperties(nebulaJob.payment_info),
    deliveryValidations: nebulaJob.delivery_validations?.length
      ? nebulaJob.delivery_validations.map((validation) => convertObjectSnakeCaseToCamelCaseProperties(validation))
      : null,
    pickUpCode: nebulaJob.pick_up_code,
    packages: nebulaJob.packages.length
      ? nebulaJob.packages.map((_package) => convertObjectSnakeCaseToCamelCaseProperties(_package))
      : [],
    deliveryOptions: nebulaJob.delivery_options.length
      ? nebulaJob.delivery_options.map((option) => convertObjectSnakeCaseToCamelCaseProperties(option))
      : []
  }
  if (job.paymentInfo?.payment && nebulaJob.payment_info?.payment) {
    job.paymentInfo.payment.metadata = nebulaJob.payment_info.payment.metadata || null
  }
  return job
}

const buildLocation = (data: LocationResponse): Location => {
  const { coordinates } = data
  delete data.coordinates

  const location: Location = convertObjectSnakeCaseToCamelCaseProperties(data)
  location.latitude = coordinates.lat
  location.longitude = coordinates.lng

  return location
}

const getItemsStatusCount = (items: Item[]) => {
  return items.reduce(
    (count, item) => {
      switch (item.state) {
        case ItemState.PENDING:
          count.pending++
          break
        case ItemState.ADDED:
          count.added++
          break
        case ItemState.REMOVED:
        case ItemState.CANCELLED:
          count.removed++
          break
        case ItemState.REJECTED:
          count.rejected++
          break
        default:
          break
      }
      return count
    },
    {
      added: 0,
      pending: 0,
      rejected: 0,
      removed: 0
    }
  )
}

const mapItemToJobItem = (item: Item): JobItem => {
  return {
    attributes: {
      ...item.attributes,
      pickingIndex: item.attributes.picking_index
        ? parseInt(item.attributes.picking_index as string) || undefined
        : undefined
    },
    comment: item.comment ?? '',
    foundQuantity: item.found_quantity,
    name: item.name,
    origin: item.origin,
    photoUrl: item.photo_url,
    presentation: {
      name: item.presentation.name,
      unit: item.presentation.unit,
      price: {
        amount: item.presentation.price.amount,
        currencyCode: item.presentation.price.currency_code
      },
      weight: item.presentation.weight ?? 0,
      quantity: item.presentation.quantity,
      dimensions: item.presentation.dimensions ?? []
    },
    status: item.state,
    quantity: item.quantity,
    isSubstitute: item.is_substitute,
    replacedBy: item.replaced_by ?? null,
    reference: item.reference,
    packageReference: item.package_reference ?? null,
    presentationV2: {
      unit: item.presentation_v2.unit,
      price: item.presentation_v2.price,
      volume: item.presentation_v2.volume ?? 0,
      weight: item.presentation_v2.weight ?? 0,
      formats: item.presentation_v2.formats ?? null,
      subUnit: item.presentation_v2.sub_unit,
      quantity: item.presentation_v2.quantity,
      subQuantity: item.presentation_v2.sub_quantity
    },
    barcodes: item.barcodes ?? [],
    rejected: {
      rejectedByPicker: item.rejected_quantity.rejected_by_picker,
      rejectedByDriver: item.rejected_quantity.rejected_by_driver,
      rejectedByUser: item.rejected_quantity.rejected_by_user
    },
    taskId: item.picking_task_id,
    packageId: item.package_id ?? null,
    quantityFoundLimits: item.quantity_found_limits
      ? {
          max: item.quantity_found_limits.max ?? null,
          min: item.quantity_found_limits.min ?? null
        }
      : { max: null, min: null },
    claimInformation: item.claim_information
      ? {
          maxClaimDate: item.claim_information.max_claim_date,
          availableClaimActions: item.claim_information.available_claim_actions
        }
      : undefined,
    rejectionInformation: item.rejection_information
      ? {
          rejectionReason: item.rejection_information.rejection_reason ?? '',
          claimAction: item.rejection_information.claim_action ?? null,
          claimState: item.rejection_information.claim_state ?? null,
          comment: item.rejection_information.comment ?? '',
          evidence: item.rejection_information.evidence ?? []
        }
      : undefined,
    state: item.state,
    itemId: item.id
  }
}

const mapTaskModelToTask = (task: TaskNebula): Task => {
  return {
    uuid: task.id,
    name: task.type,
    status: task.state,
    departments: task.departments,
    confirmPayment: false,
    optimalStartTime: task.optimal_start_time ? task.optimal_start_time : undefined,
    optimalEndTime: task.optimal_end_time ? task.optimal_end_time : undefined,
    fleet: task.fleet?.id ?? undefined,
    payment: task.payment,
    route: task.route ? convertObjectSnakeCaseToCamelCaseProperties(task.route) : null,
    realStartTime:
      task.steps.length > 0 && task.steps[0].actual_start_time ? task.steps[0].actual_start_time : undefined,
    realEndTime:
      task.steps.length > 0 && task.steps[task.steps.length - 1].actual_end_time
        ? task.steps[task.steps.length - 1].actual_end_time
        : undefined,
    resources: getTaskResource(task),
    storeId: task.store_id,
    stopMetadata: task.stop_metadata ? convertObjectSnakeCaseToCamelCaseProperties(task.stop_metadata) : null,
    steps: task.steps.map((step) => mapStepModelToStep(step))
  }
}

const mapStepModelToStep = (step: StepNebula): Step => {
  return {
    actualStart: step.actual_start_time,
    actualEnd: step.actual_end_time,
    optimalStart: step.optimal_start_time,
    optimalFinish: step.optimal_end_time,
    ordering: step.ordering,
    status: step.state,
    uuid: step.id,
    active: step.active,
    type: {
      name: step.type
    }
  }
}

const getDeliveryCodeInfo = (deliveryOptions: DeliveryOptions[]): DeliveryCodeInfo => {
  const deliveryCodeOption = deliveryOptions.find((option) => option.method === DeliveryOptionKeys.DELIVERY_CODE)

  if (deliveryCodeOption) {
    return {
      deliveryCode: deliveryCodeOption.delivery_code as string,
      deliveryCodeTries: deliveryCodeOption.delivery_code_tries as number,
      deliveryCodeTriesLimit: deliveryCodeOption.delivery_code_tries_limit as number
    }
  }

  return null
}

const mapTaskResource = (task: TaskNebula, resource: ResourceInformation): TaskResource => {
  return {
    firstName: `${resource.first_name} ${resource.last_name}`,
    lastName: '',
    phoneNumber: resource.phone_number,
    photoUrl: resource.photo_url,
    fleetId: task.fleet?.name ?? undefined,
    resourceId: resource.id
  }
}

const getTaskResource = (task: TaskNebula): TaskResource | [] => {
  let taskResource: TaskResource | [] = []
  const shouldSeeLastAssignedResource = task.state === TaskState.FINISHED || task.state === TaskState.CANCELED

  if (task.resource_information && !shouldSeeLastAssignedResource) {
    taskResource = mapTaskResource(task, task.resource_information)
  } else if (task.last_assigned_resource_information && shouldSeeLastAssignedResource) {
    taskResource = mapTaskResource(task, task.last_assigned_resource_information)
  }

  return taskResource
}
